"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import {
  createContext,
  useContext,
  type ComponentPropsWithoutRef,
  type MouseEvent,
  type ReactNode,
} from "react";

const RowHrefContext = createContext<string | null>(null);

/** Whether the caller renders inside a {@link TrLink}: a control there that
 *  takes its own click keeps the row from opening (see the row's onClick), so
 *  one that can do without the click should (Timestamp). */
export function useInRowLink(): boolean {
  return useContext(RowHrefContext) !== null;
}

/**
 * A table row that opens a page. The row stays a ROW: its cells keep their
 * association with the column headers and table navigation keeps working, so
 * the link is a real one in the cell that names the record — wrap that cell's
 * content in {@link RowLink}. Clicking anywhere else on the row is a pointer
 * shortcut layered on top (a link or control inside keeps its own behavior),
 * ⌘/ctrl-click opens a tab, and hovering prefetches the way `<Link>` does.
 *
 * The row itself carries no `role` and no `tabIndex`. `role="link"` there
 * overrides the implicit `row`, which drops every cell out of the rowgroup:
 * a screen reader then announces one run-on string instead of "row 3 of 40,
 * item: …", and loses the headers for the whole table, not just that row.
 */
export function TrLink({
  href,
  className = "",
  children,
  ...props
}: Omit<
  ComponentPropsWithoutRef<"tr">,
  // The row's own behavior: a spread lands after these and would win silently.
  "onClick" | "onMouseEnter"
> & { href: string }) {
  const router = useRouter();
  return (
    <RowHrefContext.Provider value={href}>
      <tr
        onClick={(e: MouseEvent<HTMLTableRowElement>) => {
          const target = e.target as HTMLElement;
          if (target.closest("a, button, input, select, textarea, label"))
            return;
          if (e.metaKey || e.ctrlKey) {
            window.open(href, "_blank", "noopener");
            return;
          }
          router.push(href);
        }}
        onMouseEnter={() => router.prefetch(href)}
        className={`cursor-pointer transition-colors hover:bg-bg/40 focus-within:bg-bg/40 ${className}`.trim()}
        {...props}
      >
        {children}
      </tr>
    </RowHrefContext.Provider>
  );
}

/**
 * The row's link, in the cell that names the record: what a keyboard or
 * screen-reader user follows, and where the row's accessible name comes from.
 * Outside a {@link TrLink} it renders its children plainly, so a table whose
 * rows don't all lead somewhere can use one cell shape throughout.
 */
export function RowLink({
  children,
  className = "",
}: {
  children: ReactNode;
  className?: string;
}) {
  const href = useContext(RowHrefContext);
  if (!href) return <>{children}</>;
  return (
    <Link
      href={href}
      className={`text-link rounded-xs focus-visible:outline-none ${className}`.trim()}
    >
      {children}
    </Link>
  );
}
